import { useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
            <h2 className="mt-4 text-xl font-heading font-bold text-foreground-900">漫画未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const categoryObj = categories.find((c) => c.slug === comic.category);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://yytoon.github.io/article/${comic.id}`,
    image: comic.coverImage,
    genre: [comic.categoryName],
    datePublished: '2026-01-01',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: 'yy漫画',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 5,
      ratingCount: Math.floor(comic.views / 100),
    },
  };

  const fullDescription = `${comic.description}

《${comic.title}》是${comic.author}创作的精彩${comic.categoryName}漫画作品。故事以其独特的叙事手法和精美画风获得了读者的广泛好评，在yy漫画平台上的评分为${comic.rating}分，累计阅读量超过${(comic.views / 10000).toFixed(0)}万次。

作为yy漫画正版授权平台的热门作品，《${comic.title}》目前处于${comic.status}状态，最新章节持续更新中。yy漫画致力于为漫迷提供最优质的在线阅读体验，所有漫画均获得官方正版授权，支持多端同步阅读，每日更新不间断。无论您喜欢恋爱、热血、悬疑还是奇幻类型的漫画，yy漫画都能满足您的阅读需求。`;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="px-4 md:px-6">
            <article className="flex flex-col lg:flex-row gap-8 md:gap-10 py-6 md:py-8">
              <div className="w-full lg:w-[320px] shrink-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - yy漫画正版授权`}
                    title={`${comic.title} - ${comic.author}作品 | yy漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                    {comic.categoryName}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs bg-background-100 text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {comic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    <strong className="text-foreground-900">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> 更新于 {comic.updateTime}
                  </span>
                </div>

                <div className="mt-6 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{fullDescription}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    开始阅读第一话
                  </button>
                  <button className="px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
                    加入收藏
                  </button>
                </div>
              </div>
            </article>

            <section className="py-8 md:py-10 border-t border-background-200/70">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                {comic.chapters.map((ch) => (
                  <div
                    key={ch.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800 font-medium truncate">{ch.title}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {ch.isNew && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-accent-500 text-background-50">NEW</span>
                      )}
                      <span className="text-xs text-foreground-500">{ch.updateTime}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="py-8 md:py-10 border-t border-background-200/70">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-heading text-xl font-bold text-foreground-900">相关推荐</h2>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    查看更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}